/**
 * useAccessManagement
 * 
 * Hooks for managing user roles and permissions.
 */

import { useCallback } from "react";
import { useQuery, useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// TYPES
// =============================================================================

export interface PermissionInfo {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
}

export interface RoleInfo {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: PermissionInfo[];
}

export interface UserRoleInfo {
  id: string;
  roleId: string;
  roleCode: string;
  roleName: string;
  grantedAt: string;
  grantedBy: string | null;
  expiresAt: string | null;
}

export interface UserPermissionInfo {
  id: string;
  permissionId: string;
  permissionCode: string;
  permissionName: string;
  granted: boolean;
  grantedAt: string;
  grantedBy: string | null;
  expiresAt: string | null;
  reason: string | null;
}

export interface UserWithAccess {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string;
  roles: UserRoleInfo[];
  directPermissions: UserPermissionInfo[];
}

export interface AccessManagementResult {
  success: boolean;
  message: string | null;
}

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const AdminPermissionsDocument = `
  query AdminPermissions {
    adminPermissions {
      id
      code
      name
      description
      category
    }
  }
`;

const AdminRolesDocument = `
  query AdminRoles {
    adminRoles {
      id
      code
      name
      description
      isSystem
      permissions {
        id
        code
        name
        category
      }
    }
  }
`;

const AdminUserAccessDocument = `
  query AdminUserAccess($accountId: ID!) {
    adminUserAccess(accountId: $accountId) {
      id
      firstName
      lastName
      username
      telegramId
      roles {
        id
        roleId
        roleCode
        roleName
        grantedAt
        grantedBy
        expiresAt
      }
      directPermissions {
        id
        permissionId
        permissionCode
        permissionName
        granted
        grantedAt
        grantedBy
        expiresAt
        reason
      }
    }
  }
`;

const AdminUsersWithAccessDocument = `
  query AdminUsersWithAccess($permissionCode: String, $roleCode: String, $limit: Int) {
    adminUsersWithAccess(permissionCode: $permissionCode, roleCode: $roleCode, limit: $limit) {
      id
      firstName
      lastName
      username
      telegramId
      roles {
        id
        roleId
        roleCode
        roleName
        grantedAt
        expiresAt
      }
      directPermissions {
        id
        permissionId
        permissionCode
        permissionName
        granted
        grantedAt
        expiresAt
        reason
      }
    }
  }
`;

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const AdminAssignRoleDocument = `
  mutation AdminAssignRole($accountId: ID!, $roleCode: String!, $expiresAt: DateTime) {
    adminAssignRole(accountId: $accountId, roleCode: $roleCode, expiresAt: $expiresAt) {
      success
      message
    }
  }
`;

const AdminRevokeRoleDocument = `
  mutation AdminRevokeRole($accountId: ID!, $roleCode: String!) {
    adminRevokeRole(accountId: $accountId, roleCode: $roleCode) {
      success
      message
    }
  }
`;

const AdminGrantPermissionDocument = `
  mutation AdminGrantPermission($accountId: ID!, $permissionCode: String!, $reason: String, $expiresAt: DateTime) {
    adminGrantPermission(accountId: $accountId, permissionCode: $permissionCode, reason: $reason, expiresAt: $expiresAt) {
      success
      message
    }
  }
`;

const AdminRevokePermissionDocument = `
  mutation AdminRevokePermission($accountId: ID!, $permissionCode: String!) {
    adminRevokePermission(accountId: $accountId, permissionCode: $permissionCode) {
      success
      message
    }
  }
`;

// =============================================================================
// HOOKS
// =============================================================================

export function useAdminPermissions() {
  const [result] = useQuery<{ adminPermissions: PermissionInfo[] }>({
    query: AdminPermissionsDocument,
  });

  return {
    permissions: result.data?.adminPermissions ?? [],
    isLoading: result.fetching,
    error: result.error,
  };
}

export function useAdminRoles() {
  const [result] = useQuery<{ adminRoles: RoleInfo[] }>({
    query: AdminRolesDocument,
  });

  return {
    roles: result.data?.adminRoles ?? [],
    isLoading: result.fetching,
    error: result.error,
  };
}

export function useAdminUserAccess(accountId: string | null) {
  const [result, reexecute] = useQuery<{ adminUserAccess: UserWithAccess | null }>({
    query: AdminUserAccessDocument,
    variables: { accountId },
    pause: !accountId,
  });

  return {
    user: result.data?.adminUserAccess ?? null,
    isLoading: result.fetching,
    error: result.error,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useAdminUsersWithAccess(filters?: {
  permissionCode?: string;
  roleCode?: string;
  limit?: number;
}) {
  const [result, reexecute] = useQuery<{ adminUsersWithAccess: UserWithAccess[] }>({
    query: AdminUsersWithAccessDocument,
    variables: {
      permissionCode: filters?.permissionCode,
      roleCode: filters?.roleCode,
      limit: filters?.limit ?? 50,
    },
  });

  return {
    users: result.data?.adminUsersWithAccess ?? [],
    isLoading: result.fetching,
    error: result.error,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useAssignRole() {
  const [, executeMutation] = useMutation<
    { adminAssignRole: AccessManagementResult },
    { accountId: string; roleCode: string; expiresAt?: string }
  >(AdminAssignRoleDocument);

  const assignRole = useCallback(
    async (accountId: string, roleCode: string, expiresAt?: string) => {
      try {
        const result = await executeMutation({ accountId, roleCode, expiresAt });
        if (result.error) {
          errorTracking.captureError(result.error);
          return { success: false, message: "Ошибка при назначении роли" };
        }
        return result.data?.adminAssignRole ?? { success: false, message: "Нет ответа" };
      } catch (error) {
        errorTracking.captureError(error);
        return { success: false, message: "Ошибка при назначении роли" };
      }
    },
    [executeMutation]
  );

  return { assignRole };
}

export function useRevokeRole() {
  const [, executeMutation] = useMutation<
    { adminRevokeRole: AccessManagementResult },
    { accountId: string; roleCode: string }
  >(AdminRevokeRoleDocument);

  const revokeRole = useCallback(
    async (accountId: string, roleCode: string) => {
      try {
        const result = await executeMutation({ accountId, roleCode });
        if (result.error) {
          errorTracking.captureError(result.error);
          return { success: false, message: "Ошибка при отзыве роли" };
        }
        return result.data?.adminRevokeRole ?? { success: false, message: "Нет ответа" };
      } catch (error) {
        errorTracking.captureError(error);
        return { success: false, message: "Ошибка при отзыве роли" };
      }
    },
    [executeMutation]
  );

  return { revokeRole };
}

export function useGrantPermission() {
  const [, executeMutation] = useMutation<
    { adminGrantPermission: AccessManagementResult },
    { accountId: string; permissionCode: string; reason?: string; expiresAt?: string }
  >(AdminGrantPermissionDocument);

  const grantPermission = useCallback(
    async (
      accountId: string,
      permissionCode: string,
      reason?: string,
      expiresAt?: string
    ) => {
      try {
        const result = await executeMutation({
          accountId,
          permissionCode,
          reason,
          expiresAt,
        });
        if (result.error) {
          errorTracking.captureError(result.error);
          return { success: false, message: "Ошибка при выдаче разрешения" };
        }
        return (
          result.data?.adminGrantPermission ?? { success: false, message: "Нет ответа" }
        );
      } catch (error) {
        errorTracking.captureError(error);
        return { success: false, message: "Ошибка при выдаче разрешения" };
      }
    },
    [executeMutation]
  );

  return { grantPermission };
}

export function useRevokePermission() {
  const [, executeMutation] = useMutation<
    { adminRevokePermission: AccessManagementResult },
    { accountId: string; permissionCode: string }
  >(AdminRevokePermissionDocument);

  const revokePermission = useCallback(
    async (accountId: string, permissionCode: string) => {
      try {
        const result = await executeMutation({ accountId, permissionCode });
        if (result.error) {
          errorTracking.captureError(result.error);
          return { success: false, message: "Ошибка при отзыве разрешения" };
        }
        return (
          result.data?.adminRevokePermission ?? { success: false, message: "Нет ответа" }
        );
      } catch (error) {
        errorTracking.captureError(error);
        return { success: false, message: "Ошибка при отзыве разрешения" };
      }
    },
    [executeMutation]
  );

  return { revokePermission };
}
